import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandL,
  BrandXXS,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { LineupBonusesExplanation } from '#src/components/compose/RulesPrizePoolDrawer/LineupBonusesExplanation/index.tsx';

import type { ComposeBonuses_so5AppearanceBonusInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-end;
`;
const InlineRow = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr min-content;
  column-gap: var(--double-unit);
  align-items: center;

  & > :last-child {
    align-self: start;
  }
`;
const Bonus = styled(BrandL)`
  position: relative;
  color: var(--c-white);
  text-align: right;
  text-shadow: 0 0 10px var(--c-white);
`;
const BonusInfo = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-end;
  text-align: right;
`;
const BonusIcon = styled.img`
  .disabled & {
    filter: grayscale(1) brightness(3);
    opacity: 0.4;
  }
`;
const TooltipWrapper = styled.div`
  max-width: 340px;
`;
const BonusExplanationWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  padding: var(--unit);
  ${regularMaterial}
`;

type Props = {
  customTitle?: ReactNode;
  cap?: number | null;
  total?: number;
  lineupBonuses: ComposeBonuses_so5AppearanceBonusInterface[];
  inline?: boolean;
};
export const ComposeBonuses = ({
  customTitle,
  cap = 0,
  total = 0,
  lineupBonuses,
  inline,
}: Props) => {
  if (lineupBonuses.length === 0) {
    return null;
  }

  const activeBonus =
    lineupBonuses.reduce(
      (sum, { bonusBasisPoints }) => sum + bonusBasisPoints,
      0
    ) / 10000;

  return inline ? (
    <Vertical gap={2}>
      {customTitle || (
        <LabelL bold>
          <FormattedMessage {...fantasy.bonuses} />
        </LabelL>
      )}
      <Vertical gap={3}>
        {lineupBonuses?.map(
          ({
            iconUrl,
            title,
            description,
            bonusBasisPoints,
            maxBonusBasisPoints,
          }) => {
            const disabled = bonusBasisPoints !== maxBonusBasisPoints;

            return (
              <InlineRow key={title} className={classNames({ disabled })}>
                <BonusIcon src={iconUrl} width={36} alt={title} />
                <Vertical gap={0.5}>
                  <BrandXXS
                    color={disabled ? 'var(--c-nd-500)' : 'var(--c-white)'}
                  >
                    {title}
                  </BrandXXS>
                  <BodyS
                    color={disabled ? 'var(--c-nd-500)' : 'var(--c-nd-700)'}
                  >
                    {description}
                  </BodyS>
                </Vertical>
                <BrandXXS
                  color={
                    disabled ? 'var(--c-nd-700)' : 'var(--c-onBase-success)'
                  }
                >
                  <FormattedNumber
                    value={maxBonusBasisPoints / 10000}
                    signDisplay="always"
                    style="percent"
                  />
                </BrandXXS>
              </InlineRow>
            );
          }
        )}
      </Vertical>
    </Vertical>
  ) : (
    <Root>
      <Dropdown
        side="left"
        triggerOnHover
        label={props => (
          <Vertical {...props} gap={0.5}>
            <Bonus>
              <AnimatedNumber
                value={activeBonus}
                formatNumberProps={{ signDisplay: 'always', style: 'percent' }}
              />
            </Bonus>
            <Horizontal gap={0.5}>
              <BrandXXS color="var(--c-nd-700)">
                <FormattedMessage defaultMessage="Lineup Bonus" />
              </BrandXXS>
              <FontAwesomeIcon
                icon={faInfoCircle}
                color="var(--c-nd-700)"
                size="xs"
              />
            </Horizontal>
          </Vertical>
        )}
      >
        <TooltipWrapper>
          <LineupBonusesExplanation lineupBonuses={lineupBonuses} />
        </TooltipWrapper>
      </Dropdown>
      {lineupBonuses.map(lineupBonus => {
        const {
          iconUrl,
          title,
          description,
          bonusBasisPoints,
          maxBonusBasisPoints,
        } = lineupBonus;
        const disabled = bonusBasisPoints !== maxBonusBasisPoints;

        return (
          <Horizontal key={title} className={classNames({ disabled })}>
            <BonusInfo>
              <BrandXXS color={disabled ? 'var(--c-nd-500)' : 'var(--c-white)'}>
                {title}
              </BrandXXS>
              <Horizontal>
                {isType(lineupBonus, 'So5AverageScoresBonus') && (
                  <Horizontal>
                    <BrandXXS color="var(--c-nd-600)">
                      <span style={{ color: 'var(--c-white)' }}>{total}</span>/
                      {cap}
                    </BrandXXS>
                  </Horizontal>
                )}
                <BrandXXS
                  color={
                    disabled ? 'var(--c-nd-500)' : 'var(--c-onBase-success)'
                  }
                >
                  <FormattedNumber
                    value={maxBonusBasisPoints / 10000}
                    signDisplay="always"
                    style="percent"
                  />
                </BrandXXS>
              </Horizontal>
            </BonusInfo>
            <Dropdown
              triggerOnHover
              side="left"
              gap={8}
              label={props => (
                <BonusIcon {...props} src={iconUrl} width={36} alt={title} />
              )}
            >
              <BonusExplanationWrapper>
                <BrandXXS>{title}</BrandXXS>
                <BodyS>{description}</BodyS>
              </BonusExplanationWrapper>
            </Dropdown>
          </Horizontal>
        );
      })}
    </Root>
  );
};

ComposeBonuses.fragments = {
  so5AppearanceBonusInterface: gql`
    fragment ComposeBonuses_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      iconUrl
      title
      description
      maxBonusBasisPoints
      bonusBasisPoints
      ...LineupBonusesExplanation_so5AppearanceBonusInterface
    }
    ${LineupBonusesExplanation.fragments.so5AppearanceBonusInterface}
  ` as TypedDocumentNode<ComposeBonuses_so5AppearanceBonusInterface>,
};
